/**
 * Sequence Merging Utility
 *
 * Merges two sequences by finding where a suffix of the older sequence
 * matches a prefix of the newer sequence, then deduplicating that overlap.
 *
 * The result structure is:
 *   [older-only prefix] + [overlapping subsequence] + [newer-only suffix]
 *
 * Example 1 (with overlap):
 *   older:  ABCDE
 *   newer:  BCDEFGHI
 *   overlap: BCDE (suffix of older = prefix of newer)
 *   merged: A + BCDE + FGHI = ABCDEFGHI
 *
 * Example 2 (no overlap):
 *   older:  ABCDE
 *   newer:  GHI
 *   overlap: (none)
 *   merged: ABCDE + GHI = ABCDEGHI
 */

import { ITranscriptionMessage } from "../hexagon/core/ports/HealthScribe";

/**
 * Default equality function for transcription messages.
 * Two messages are considered equal if they have the same content (case insensitive) and speaker.
 */
export const defaultTranscriptionEquals = (
  a: ITranscriptionMessage,
  b: ITranscriptionMessage
): boolean => {
  // Normalize speaker capitalization and normalize speakers that all mean unknown
  // Older backend would send "UNKNOWN" as the speaker; newer backend sends "talking".
  let speakerA = a.speaker.toLowerCase();
  if (speakerA === "unknown") {
    speakerA = "talking";
  }
  let speakerB = b.speaker.toLowerCase();
  if (speakerB === "unknown") {
    speakerB = "talking";
  }
  return (
    a.message.toLowerCase() === b.message.toLowerCase() && speakerA === speakerB
  );
};

/**
 * Merge two sequences by finding and deduplicating the overlapping subsequence.
 *
 * The algorithm:
 * 1. Find the longest suffix of olderSequence that matches a prefix of newerSequence
 * 2. Return: olderSequence + newerSequence (with overlapping prefix removed)
 *
 * Common use cases:
 * - Merging localStorage (older) with backend/cloud (newer)
 * - Merging existing data with newly received real-time data
 * - Merging Redux state with incoming updates
 *
 * @param olderSequence First sequence (assumed to be older/stale data)
 * @param newerSequence Second sequence (assumed to be newer/fresher data)
 * @param equals Equality comparison function
 * @returns Merged sequence: older + newer with overlapping subsequence deduplicated
 */
export function mergeSequences<T>(
  olderSequence: T[],
  newerSequence: T[],
  equals: (a: T, b: T) => boolean
): T[] {
  // Find the longest suffix of older that matches a prefix of newer
  const maxPossibleOverlap = Math.min(
    olderSequence.length,
    newerSequence.length
  );

  let overlapLength = 0;

  // Try each possible overlap length, starting from longest
  for (let len = maxPossibleOverlap; len > 0; len--) {
    // Check if last `len` elements of older equal first `len` elements of newer
    let matches = true;
    for (let i = 0; i < len; i++) {
      const olderIdx = olderSequence.length - len + i;
      if (!equals(olderSequence[olderIdx], newerSequence[i])) {
        matches = false;
        break;
      }
    }

    if (matches) {
      overlapLength = len;
      break; // Found the longest overlap
    }
  }

  // Result is: older + (newer with overlapping prefix removed)
  return [...olderSequence, ...newerSequence.slice(overlapLength)];
}

/**
 * Merge two transcription message sequences.
 * Tries both merge orders and returns the shorter result to handle
 * cases where the caller isn't certain which sequence is older/newer.
 *
 * @param transcriptionsA First transcription sequence
 * @param transcriptionsB Second transcription sequence
 * @param equals Optional custom equality function
 * @returns Merged transcription sequence (shortest valid merge)
 */
export function mergeTranscriptions(
  transcriptionsA: ITranscriptionMessage[],
  transcriptionsB: ITranscriptionMessage[],
  equals: (
    a: ITranscriptionMessage,
    b: ITranscriptionMessage
  ) => boolean = defaultTranscriptionEquals
): ITranscriptionMessage[] {
  // Try both orders and return the shorter result
  const resultAB = mergeSequences(transcriptionsA, transcriptionsB, equals);
  const resultBA = mergeSequences(transcriptionsB, transcriptionsA, equals);

  // Prefer A-then-B order if lengths are equal
  if (resultAB.length <= resultBA.length) {
    return resultAB;
  }
  return resultBA;
}
